import i18n, { type BackendModule } from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";

import {
  DEFAULT_LANGUAGE,
  LANGUAGE_STORAGE_KEY,
  SUPPORTED_LANGUAGES,
  directionOf,
  isSupportedLanguage,
  type LanguageCode,
} from "./languages";
import { NAMESPACES, defaultNS, enResources } from "./resources";

// Non-English catalogs are split into their own chunks and fetched on demand,
// so a French visitor never downloads the Arabic strings (and vice versa).
const lazyCatalogs = import.meta.glob<{ default: Record<string, unknown> }>(
  ["./locales/*/*.json", "!./locales/en/*.json"],
);

const lazyBackend: BackendModule = {
  type: "backend",
  init() {},
  read(language, namespace, callback) {
    const load = lazyCatalogs[`./locales/${language}/${namespace}.json`];
    if (!load) {
      callback(null, {});
      return;
    }
    load().then(
      (mod) => callback(null, mod.default),
      (err: unknown) => callback(err as Error, null),
    );
  },
};

function syncDocument(lng: string) {
  const root = document.documentElement;
  root.lang = lng;
  root.dir = directionOf(lng);
}

i18n.on("languageChanged", syncDocument);

export const i18nReady = i18n
  .use(lazyBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: { en: enResources },
    // Keep the bundled English catalog and lazy-load everything else.
    partialBundledLanguages: true,
    ns: NAMESPACES,
    defaultNS,
    fallbackLng: DEFAULT_LANGUAGE,
    supportedLngs: SUPPORTED_LANGUAGES,
    nonExplicitSupportedLngs: true,
    load: "languageOnly",
    detection: {
      order: ["localStorage", "navigator"],
      lookupLocalStorage: LANGUAGE_STORAGE_KEY,
      caches: ["localStorage"],
    },
    interpolation: {
      // React already escapes rendered strings.
      escapeValue: false,
    },
    react: {
      // Every namespace is loaded before first render and before a language
      // switch resolves, so components never need to suspend.
      useSuspense: false,
    },
    returnNull: false,
  });

/** The active app language, normalized to one we ship (e.g. "fr-CA" → "fr"). */
export function currentLanguage(): LanguageCode {
  const lng = i18n.resolvedLanguage ?? i18n.language;
  return lng && isSupportedLanguage(lng) ? lng : DEFAULT_LANGUAGE;
}

export function setLanguage(code: LanguageCode) {
  return i18n.changeLanguage(code);
}

export default i18n;
